// src/components/CreatePost.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { ExternalLink, Plus, X, Send, Loader2 } from 'lucide-react';
import { COUNTRIES } from '../config/feeds';
import { useI18n } from '../i18n';
import { openInAppBrowser } from '../utils/browser';

const CreatePost = ({ onSubmit, isLoading, country = 'US' }) => {
  const { t } = useI18n();
  const [postText, setPostText] = useState('');
  const [sources, setSources] = useState([{ url: '' }]);
  const [error, setError] = useState('');

  // Get country-specific government domains
  const countryConfig = useMemo(() => {
    return COUNTRIES[country] || COUNTRIES.US;
  }, [country]);

  const govDomains = useMemo(() => {
    return countryConfig.govDomains || ['.gov'];
  }, [countryConfig]);

  const validateUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      // Check if hostname ends with any of the allowed government domains
      const isGovDomain = govDomains.some(domain => hostname.endsWith(domain));
      return isGovDomain || hostname === 'govglance.org';
    } catch {
      return false;
    }
  };

  // Build regex pattern for URL detection based on country domains
  const urlRegexPattern = useMemo(() => {
    const domainPatterns = govDomains.map(d => d.replace('.', '\\.')).join('|');
    return new RegExp(`(https?:\\/\\/[^\\s]+?(?:${domainPatterns}|govglance\\.org)[^\\s]*)`, 'gi');
  }, [govDomains]);

  // Auto-detect URLs from text
  useEffect(() => {
    const matches = postText.match(urlRegexPattern) || [];
    
    // Filter out duplicates and validate URLs
    const validUrls = [...new Set(matches)]
      .filter(url => validateUrl(url))
      .map(url => ({ url }));

    // Merge with existing manually added sources
    const existingManualSources = sources.filter(source => 
      source.url && !matches.includes(source.url)
    );

    const newSources = [...validUrls, ...existingManualSources];
    
    // Always ensure at least one empty input if no sources
    if (newSources.length === 0) {
      newSources.push({ url: '' });
    }

    setSources(newSources);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postText, urlRegexPattern]);

  const handleAddSource = () => {
    setSources([...sources, { url: '' }]);
  };

  const handleRemoveSource = (index) => {
    const newSources = sources.filter((_, i) => i !== index);
    // Ensure at least one source input remains
    if (newSources.length === 0) {
      newSources.push({ url: '' });
    }
    setSources(newSources);
  };

  const handleSourceChange = (index, value) => {
    const newSources = sources.map((source, i) => {
      if (i === index) {
        return { url: value };
      }
      return source;
    });
    setSources(newSources);
    setError('');
  };

  // Format domain display for error messages
  const domainDisplayText = useMemo(() => {
    if (govDomains.length === 1) {
      return govDomains[0];
    }
    const last = govDomains[govDomains.length - 1];
    const rest = govDomains.slice(0, -1);
    return `${rest.join(', ')} or ${last}`;
  }, [govDomains]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Filter out empty sources
    const validSources = sources.filter(source => source.url.trim());
    
    if (validSources.length === 0) {
      setError(t('createPost.errorNoSource'));
      return;
    }

    // Validate all sources
    const invalidSources = validSources.filter(source => !validateUrl(source.url));
    if (invalidSources.length > 0) {
      setError(t('createPost.errorInvalidSource'));
      return;
    }

    // Create post text with sources
    const sourcesList = validSources.map(source => source.url).join('\n');
    const fullPostText = `${postText}\n\nSources:\n${sourcesList}`;
    
    onSubmit(fullPostText);
    setPostText('');
    setSources([{ url: '' }]);
    setError('');
  };

  const getSourceType = (url) => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      if (hostname === 'govglance.org') return 'govglance';
      if (govDomains.some(domain => hostname.endsWith(domain))) return 'gov';
      return null;
    } catch {
      return null;
    }
  };

  return (
    <div className="create-post">
      <div className="govglance-banner">
        <button 
          type="button"
          className="govglance-link"
          onClick={() => openInAppBrowser('https://govglance.org')}
        >
          <ExternalLink size={14} />
          {t('createPost.findSources')}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="post-form">
        <textarea
          placeholder={t('createPost.placeholder')}
          value={postText}
          onChange={(e) => setPostText(e.target.value)}
          disabled={isLoading}
          className="post-input"
        />
        
        <div className="sources-section">
          <div className="sources-header">
            <h3>{t('createPost.sources')}</h3>
            <span className="sources-hint">{domainDisplayText} {t('createPost.sourcesHint')}</span>
          </div>
          
          {sources.map((source, index) => {
            const sourceType = getSourceType(source.url);
            return (
              <div key={index} className="source-input-group">
                <div className="source-input-wrapper">
                  <input
                    type="url"
                    placeholder="https://..."
                    value={source.url}
                    onChange={(e) => handleSourceChange(index, e.target.value)}
                    disabled={isLoading}
                    className={`source-input ${sourceType ? `source-type-${sourceType}` : ''}`}
                  />
                  {sourceType && (
                    <span className={`source-badge ${sourceType}`}>
                      {sourceType === 'govglance' ? 'GovGlance' : '.gov'}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveSource(index)}
                  className="remove-source-button"
                  disabled={isLoading || sources.length === 1}
                  aria-label="Remove source"
                >
                  <X size={16} />
                </button>
              </div>
            );
          })}
          
          <button
            type="button"
            onClick={handleAddSource}
            className="button button-secondary add-source-button"
            disabled={isLoading}
          >
            <Plus size={14} />
            {t('createPost.addSource')}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button 
          type="submit" 
          className="button button-primary submit-post-button"
          disabled={isLoading || !postText.trim() || sources.every(s => !s.url.trim())}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="loading-spinner" />
              {t('createPost.posting')}
            </>
          ) : (
            <>
              <Send size={16} />
              {t('createPost.post')}
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default CreatePost;